import { Injectable } from '@angular/core';
import { DvSnapshotField, DvSnapshotValue } from '@common/entities';
import { Lookup, MovedFromLocations, SnapshotDisplayMode, SnapshotFieldType } from '@common/constants';
import { LabelTextPipe, PhoneNumberPipe, SocialInsuranceNumberPipe, PostalCodePipe } from '../../index';
import { IntakeLabelCollection } from '@common/labels';
import { Utilities } from '@common/utils';

@Injectable({
  providedIn: 'root',
})
export class ApplicationSnapshotUtils {
  //   constructor() {}
  labelTextPipe = new LabelTextPipe();
  phoneNumberPipe = new PhoneNumberPipe();
  sinPipe = new SocialInsuranceNumberPipe();
  postalCodePipe = new PostalCodePipe();
  movedFromLocations = MovedFromLocations;

  createSnapshotField<T>(
    entityReference: string,
    fieldName: string,
    value: T, //this can be string or object
    displayMode = SnapshotDisplayMode.Normal,
    fieldType = SnapshotFieldType.Default,
    labelReference: IntakeLabelCollection = null,
    lookupReference: { [name: string]: Lookup } = null,
    labelName: string = null, //pass in label text or labelName property (corresponding to labelReference) if different than fieldName
    excludeLabel = false, //force label to be null so that it does not render '-' if does not exist
    labelReplacements: Record<string, string> = null //handles <<replacementText>> for spouse/partner
  ): DvSnapshotField {
    const snapshotItemValue = new DvSnapshotValue();

    if (!labelName) {
      //use fieldName if labelName is not passed in
      labelName = fieldName; //use fieldname as mapper of labelName not provided
    }
    const snapshotField = new DvSnapshotField();
    snapshotField.entityReference = entityReference;
    snapshotField.fieldReference = fieldName;
    const snapshotDisplayMode = displayMode ?? SnapshotDisplayMode.Normal;
    snapshotItemValue.fieldName = fieldName;

    snapshotItemValue.label = null;
    if (!excludeLabel) {
      snapshotItemValue.label = labelName;

      //if labelReference is passed in, grab the label from the label lookup
      if (labelReference) {
        const labelItem = labelReference[labelName];
        if (labelItem) {
          const hasThirdPersonLabel = labelItem.thirdPersonLabel ? true : false;
          //if thirdPersonalLabel property exists, it should be used for snapshot
          const labelProperty = hasThirdPersonLabel ? 'thirdPersonLabel' : 'spousePartnerLabel';
          snapshotItemValue.label = this.labelTextPipe.transformLabel(labelItem, labelReplacements, labelProperty);
        } else {
          console.log('Labels');
          console.log(labelReference);
          console.log('Replacements');
          console.log(labelReplacements);
          console.log('Could not find label item ' + labelName);
        }
      }
    }

    snapshotItemValue.displayMode = snapshotDisplayMode;
    snapshotItemValue.value = null;
    let stringValue = '';
    if (typeof value === 'string') {
      stringValue = value as string;
    }
    if (fieldType === SnapshotFieldType.Default) {
      snapshotItemValue.value = stringValue;
    } else if (fieldType === SnapshotFieldType.YesNo) {
      snapshotItemValue.value = value === true ? 'Yes' : 'No';
    } else if (fieldType === SnapshotFieldType.Date) {
      const dateValue = value as Date;
      snapshotItemValue.value = this.toLocalDateString(dateValue) ?? '';
    } else if (fieldType === SnapshotFieldType.Currency) {
      const numberValue = value as number;
      snapshotItemValue.value = this.toCurrencyString(numberValue) ?? '';
    } else if (fieldType === SnapshotFieldType.Number) {
      snapshotItemValue.value = value.toString() ?? '';
    } else if (fieldType === SnapshotFieldType.Lookup) {
      const lookupItem = lookupReference[stringValue];
      snapshotItemValue.value = lookupItem?.displayValue;
      //if there is a transform value for answer, we need to transform it here
      const thirdPartyValueProperty = 'thirdPartyValue';
      if (lookupItem && Utilities.hasValue(lookupItem[thirdPartyValueProperty])) {
        snapshotItemValue.value = this.labelTextPipe.transformAnswer(
          lookupItem,
          labelReplacements,
          thirdPartyValueProperty
        );
      }
    } else if (fieldType === SnapshotFieldType.Phone) {
      //array of phone numbers
      snapshotItemValue.value = null;
      snapshotItemValue.valueCollection = [] as DvSnapshotValue[];
      snapshotItemValue.displayMode = SnapshotDisplayMode.MultiInput; //force display mode to be multi input for phone

      const phoneNumberValues = Array.isArray(value) ? value : [value]; //collection of phone numbers
      phoneNumberValues.forEach((objPhone) => {
        const subFieldName = 'phoneNumber';
        const subValue = `${this.phoneNumberPipe.transform(objPhone.phoneNumber)} - ${objPhone.type}`;
        const subSnapshotValue = new DvSnapshotValue();
        subSnapshotValue.displayMode = SnapshotDisplayMode.Normal;
        subSnapshotValue.fieldName = subFieldName;
        subSnapshotValue.label = null;
        subSnapshotValue.value = subValue;
        snapshotItemValue.valueCollection.push(subSnapshotValue);
      });
    } else if (fieldType === SnapshotFieldType.PostalCode) {
      snapshotItemValue.value = this.postalCodePipe.transform(stringValue);
    } else if (fieldType === SnapshotFieldType.SocialInsuranceNumber) {
      snapshotItemValue.value = this.sinPipe.transform(stringValue);
    } else if (fieldType === SnapshotFieldType.Address) {
      snapshotItemValue.value = null;
      snapshotItemValue.valueCollection = [] as DvSnapshotValue[];
      const addressValues = value;
      const snapshotCollection: DvSnapshotValue[] = [];
      const propertyNames = 'streetAddress,suiteNumber,city,province,postalCode';
      propertyNames.split(',').forEach((propertyName) => {
        let snapshotValue = addressValues[propertyName];
        if (propertyName === 'postalCode') {
          //format postalcode
          snapshotValue = this.postalCodePipe.transform(snapshotValue);
        }
        const labelItem = labelReference[propertyName];
        const addressItemValue = new DvSnapshotValue();
        addressItemValue.fieldName = propertyName;
        addressItemValue.displayMode = SnapshotDisplayMode.Normal;
        addressItemValue.value = snapshotValue;
        addressItemValue.label = this.labelTextPipe.transform(labelItem);
        snapshotCollection.push(addressItemValue);
      });
      snapshotCollection.forEach((field) => {
        snapshotItemValue.valueCollection.push(field);
      });
    } else if (fieldType === SnapshotFieldType.IndianRegistrationNumber) {
      const irn = value as {
        band?: string;
        family?: string;
        position?: string;
      };
      if (irn?.band?.length > 0 && irn?.family?.length > 0 && irn?.position?.length > 0) {
        snapshotItemValue.value = `${irn?.band}-${irn?.family}-${irn?.position}`;
      }
    } else if (fieldType === SnapshotFieldType.MovedFromLocation) {
      snapshotItemValue.value = this.movedFromLocations[stringValue];
    } else if (fieldType === SnapshotFieldType.Municipality) {
      snapshotItemValue.value = value['name'];
    } else if (fieldType === SnapshotFieldType.CalloutContent) {
      //callout should never have snapshot value
      snapshotItemValue.value = null;
    }

    snapshotField.snapshotValue = snapshotItemValue;
    return snapshotField;
  }

  toLocalDateString(date?: Date) {
    if (!date) {
      return null;
    }
    const dateString = new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC', // Equivalent to '+0000'
    });
    return dateString;
  }

  toCurrencyString(value?: number, emptyReplacement?: string) {
    if (value === null || value === undefined) {
      return emptyReplacement ?? null;
    }
    const currencyString = value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    return currencyString;
  }

  toNumericString(value?: number, emptyReplacement?: string) {
    if (value === null || value === undefined) {
      return emptyReplacement ?? null;
    }
    return value.toString();
  }
}
